import React, { useCallback, useState } from "react";
import download from "downloadjs";
import Button from "@ui/Button";
import Text from "@ui/Text";
import { PlayCircleLine } from "@ui/IconV2/react";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../i18n";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { useGet } from "../../../api/useCall";
import { DateTime } from "luxon";

interface ManualFaultMigrationDryRunProps {
  canEdit: boolean;
}

export function ManualFaultMigrationDryRun({ canEdit }: ManualFaultMigrationDryRunProps) {
  const { addToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const getDryRun = useGet("/manualFaultEvent/migration/dry-run");

  const handleDryRun = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getDryRun({});
      const { total, blockedCount, csvFile } = response.body;

      const csvContent = atob(csvFile);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const fileName = `manualFaultMigration-dry-run-${DateTime.now().toISODate()}.csv`;
      download(blob, fileName);

      if (blockedCount > 0) {
        addToast(
          "warning",
          t("customization.section.failure_analysis.migration.dry_run.blocked", {
            blocked: blockedCount,
            total,
          }),
        );
      } else {
        addToast(
          "success",
          t("customization.section.failure_analysis.migration.dry_run.success", {
            total,
          }),
        );
      }
    } catch (error) {
      console.debug({ error });
    } finally {
      setIsLoading(false);
    }
  }, [getDryRun, addToast]);

  if (!canEdit) {
    return null;
  }

  return (
    <div className={migrationContainer}>
      <Text type="body1" as="p" className={textContainer}>
        <span>{t("customization.section.failure_analysis.migration.description")}</span>
        <Button
          variant="link"
          leftIcon={<PlayCircleLine />}
          onClick={handleDryRun}
          disabled={isLoading}
        >
          {isLoading
            ? t("customization.section.failure_analysis.migration.dry_run.loading")
            : t("customization.section.failure_analysis.migration.dry_run.button")}
        </Button>
      </Text>
    </div>
  );
}

const migrationContainer = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.s};
  margin-bottom: ${spacings.m};
`;
const textContainer = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;
